/**
 * All styling lives in the shadow root. Two rules carry legal weight and must not be
 * "improved" away:
 *   1. `.ck-btn--primary` and `.ck-btn--secondary` resolve to the same size and weight,
 *      so accept and reject are equivalent (DSK Orientierungshilfe).
 *   2. `:focus-visible` is always visible — the dialog is in scope of the EAA.
 */
export const STYLES = `
:host { all: initial; }
* { box-sizing: border-box; }
.ck-wrap {
  font-family: var(--ck-font);
  font-size: 14px;
  line-height: 1.55;
  color: var(--ck-text);
}
.ck-overlay {
  position: fixed;
  inset: 0;
  background: var(--ck-overlay);
  z-index: 2147483641;
  animation: ck-fade .18s ease-out;
}
.ck-panel {
  position: fixed;
  z-index: 2147483642;
  background: var(--ck-bg);
  border: 1px solid var(--ck-border);
  border-radius: var(--ck-radius);
  box-shadow: var(--ck-shadow);
  padding: 20px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  animation: ck-rise .22s ease-out;
}
.ck-panel--box { width: min(420px, calc(100vw - 32px)); }
.ck-panel--bar {
  inset-inline: 0;
  width: 100%;
  border-radius: 0;
  border-inline: 0;
}
.ck-panel--center {
  inset-block-start: 50%;
  inset-inline-start: 50%;
  transform: translate(-50%, -50%);
  width: min(520px, calc(100vw - 32px));
}
.ck-panel--center:dir(rtl) { transform: translate(50%, -50%); }
.ck-pos-bottom-left { inset-block-end: 16px; inset-inline-start: 16px; }
.ck-pos-bottom-right { inset-block-end: 16px; inset-inline-end: 16px; }
.ck-pos-bottom-center { inset-block-end: 16px; inset-inline-start: 50%; transform: translateX(-50%); }
.ck-pos-top-center { inset-block-start: 16px; inset-inline-start: 50%; transform: translateX(-50%); }
.ck-panel--bar.ck-pos-bottom-left,
.ck-panel--bar.ck-pos-bottom-right,
.ck-panel--bar.ck-pos-bottom-center { inset-block-end: 0; inset-inline-start: 0; transform: none; }
.ck-panel--bar.ck-pos-top-center { inset-block-start: 0; inset-inline-start: 0; transform: none; }
.ck-panel--bar .ck-inner { max-width: 1120px; margin: 0 auto; }
.ck-panel--compact { padding: 14px 16px; }

.ck-title { margin: 0 0 6px; font-size: 15px; font-weight: 650; letter-spacing: -0.01em; }
.ck-body { margin: 0 0 14px; color: var(--ck-text-muted); }
.ck-panel--compact .ck-body { margin-bottom: 10px; }

.ck-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.ck-actions--split { justify-content: flex-start; }
.ck-btn {
  appearance: none;
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 11px 20px;
  font: inherit;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.2;
  cursor: pointer;
  flex: 1 1 auto;
  min-width: 140px;
  text-align: center;
  transition: filter .15s ease, transform .05s ease;
}
.ck-btn:hover { filter: brightness(1.06); }
.ck-btn:active { transform: scale(.985); }
.ck-btn--primary { background: var(--ck-accent); color: var(--ck-on-accent); }
.ck-btn--secondary { background: var(--ck-secondary-bg); color: var(--ck-secondary-text); }
.ck-btn--ghost {
  background: transparent;
  color: var(--ck-text);
  border-color: var(--ck-border);
  flex: 0 1 auto;
  min-width: 0;
}
.ck-btn:focus-visible,
.ck-link:focus-visible,
.ck-toggle input:focus-visible + .ck-track {
  outline: 2px solid var(--ck-accent);
  outline-offset: 3px;
}

.ck-links { display: flex; flex-wrap: wrap; gap: 14px; margin-top: 12px; }
.ck-link { color: var(--ck-text-muted); font-size: 12.5px; text-decoration: underline; }
.ck-link:hover { color: var(--ck-text); }

.ck-cat {
  border: 1px solid var(--ck-border);
  border-radius: var(--ck-radius-sm);
  background: var(--ck-surface);
  padding: 14px 16px;
  margin-bottom: 10px;
}
.ck-cat-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.ck-cat-title { font-weight: 640; font-size: 14px; }
.ck-cat-desc { color: var(--ck-text-muted); font-size: 13px; margin-top: 4px; }
.ck-always { color: var(--ck-text-muted); font-size: 12.5px; white-space: nowrap; padding-top: 2px; }

.ck-toggle { position: relative; flex: none; display: inline-flex; }
.ck-toggle input {
  position: absolute;
  inset: 0;
  opacity: 0;
  margin: 0;
  cursor: pointer;
}
.ck-track {
  width: 42px;
  height: 24px;
  border-radius: 999px;
  background: var(--ck-accent-soft);
  border: 1px solid var(--ck-border);
  position: relative;
  transition: background .15s ease;
  pointer-events: none;
}
.ck-track::after {
  content: "";
  position: absolute;
  inset-block-start: 2px;
  inset-inline-start: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--ck-bg);
  box-shadow: 0 1px 2px rgba(0,0,0,.2);
  transition: transform .15s ease;
}
.ck-toggle input:checked + .ck-track { background: var(--ck-accent); border-color: var(--ck-accent); }
.ck-toggle input:checked + .ck-track::after { transform: translateX(18px); }
.ck-toggle input:checked + .ck-track:dir(rtl)::after { transform: translateX(-18px); }

.ck-vendors { margin-top: 12px; border-top: 1px solid var(--ck-border); padding-top: 10px; }
.ck-vendor { font-size: 12.5px; color: var(--ck-text-muted); padding: 8px 0; }
.ck-vendor + .ck-vendor { border-top: 1px dashed var(--ck-border); }
.ck-vendor-name { color: var(--ck-text); font-weight: 600; }
.ck-vendor-row { margin-top: 2px; }
.ck-badge {
  display: inline-block;
  margin-inline-start: 6px;
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--ck-accent-soft);
  color: var(--ck-text);
  font-size: 11px;
  font-weight: 600;
}
.ck-close {
  appearance: none;
  background: transparent;
  border: 0;
  color: var(--ck-text-muted);
  font-size: 20px;
  line-height: 1;
  padding: 4px 8px;
  cursor: pointer;
  border-radius: var(--ck-radius-sm);
}
.ck-prefs-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }

.ck-revisit {
  position: fixed;
  inset-block-end: 16px;
  inset-inline-start: 16px;
  z-index: 2147483639;
  appearance: none;
  border: 1px solid var(--ck-border);
  background: var(--ck-bg);
  color: var(--ck-text);
  border-radius: 999px;
  padding: 9px 14px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: var(--ck-shadow);
}
.ck-revisit:focus-visible { outline: 2px solid var(--ck-accent); outline-offset: 3px; }

@keyframes ck-fade { from { opacity: 0 } to { opacity: 1 } }
@keyframes ck-rise { from { opacity: 0; transform: translateY(8px) } to { opacity: 1 } }
@media (prefers-reduced-motion: reduce) {
  .ck-overlay, .ck-panel { animation: none; }
  .ck-btn, .ck-track, .ck-track::after { transition: none; }
}
@media (max-width: 480px) {
  .ck-panel--box, .ck-panel--center {
    inset-inline: 8px;
    inset-block-end: 8px;
    inset-block-start: auto;
    width: auto;
    transform: none;
  }
  .ck-btn { flex: 1 1 100%; }
}
`;

/**
 * Embed placeholders sit in the light DOM — they have to occupy the slot the iframe left —
 * so they carry their own rules and their own copy of the theme tokens.
 */
export const PLACEHOLDER_STYLES = `
.ck-ph {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
  padding: 24px;
  min-height: 180px;
  background: var(--ck-surface);
  border: 1px solid var(--ck-border);
  border-radius: var(--ck-radius-sm);
  color: var(--ck-text);
  font-family: var(--ck-font);
  font-size: 14px;
  line-height: 1.55;
}
.ck-ph-body { color: var(--ck-text-muted); max-width: 46ch; font-size: 13px; margin: 0; }
.ck-ph-actions { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; }
.ck-ph .ck-btn {
  appearance: none;
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 10px 18px;
  font: inherit;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
}
.ck-ph .ck-btn--primary { background: var(--ck-accent); color: var(--ck-on-accent); }
.ck-ph .ck-btn--ghost { background: transparent; color: var(--ck-text); border-color: var(--ck-border); }
.ck-ph .ck-btn:focus-visible { outline: 2px solid var(--ck-accent); outline-offset: 3px; }
`;
